import { PermissionStatus } from "./actions-hooks-types";
import { JobStatusEnum } from "@/lib/types/job";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import {
	JobDocumentEntityType,
	JobItemEntityType,
} from "@/lib/supabase/supabaseTypes";
import { useJobStore } from "../../store/Jobs";
import { JobDocumentWithFiles } from "../../store/Jobs/job-documents.store";
import { getDefaultTemplateTypeForDocument } from "../../pages/job-page/job-document/job-document-components/job-document-utils";
import { applyRounding } from "../../lib/utils/calculate";
import { RoundToType } from "../../store/Jobs/utils";

export function jobDocumentIsBooked(jobDocument: JobDocumentEntityType) {
	return (
		jobDocument.status === JobStatusEnum.BOOKED_SINGLE ||
		jobDocument.status === JobStatusEnum.BOOKED_MONTHLY
	);
}

export function jobDocumentIsPaid(jobDocument: JobDocumentEntityType) {
	return (
		jobDocument.status === JobStatusEnum.PAID_SINGLE ||
		jobDocument.status === JobStatusEnum.PAID_MONTHLY
	);
}

function jobDocumentIsArchived(jobDocument: JobDocumentEntityType) {
	return jobDocument.status === JobStatusEnum.ARCHIVED;
}

function jobDocumentIsInProgress(jobDocument: JobDocumentEntityType) {
	return jobDocument.status === JobStatusEnum.IN_PROGRESS;
}

function jobDocumentIsDeliveryNote(jobDocument: JobDocumentEntityType) {
	return jobDocument.type === JobDocumentTypeEnum.DeliveryNote;
}

function jobDocumentIsCreditNote(jobDocument: JobDocumentEntityType) {
	return jobDocument.type === JobDocumentTypeEnum.CreditNote;
}

function jobDocumentIsQuotation(jobDocument: JobDocumentEntityType) {
	return jobDocument.type === JobDocumentTypeEnum.Quotation;
}

function jobDocumentHasPdfFile(jobDocument: JobDocumentWithFiles | JobDocumentEntityType): boolean {
	if (!('files_with_shares' in jobDocument) || !jobDocument.files_with_shares || jobDocument.files_with_shares.length === 0) {
		return false;
	}

	const templateType = getDefaultTemplateTypeForDocument(
		jobDocument.type as JobDocumentTypeEnum
	);

	return jobDocument.files_with_shares.some((file) => {
		const metaData = file.meta_data as Record<string, any> | null;
		if (!metaData) return false;

		return (
			metaData.type === "pdf" &&
			metaData.pdf_template_type === templateType
		);
	});
}

/**
 * useJobDocumentActions - Custom hook to create, modify, or delete job documents
 * Can only be used within the client and job context
 */
export const useJobDocumentActions = () => {
	const { updateJobDocumentStatus, job } = useJobStore((state) => ({
		updateJobDocumentStatus: state.updateJobDocumentStatus,
		job: state.job,
	}));

	const archiveJobDocument = (jobDocument: JobDocumentEntityType) => {
		updateJobDocumentStatus(jobDocument.id, JobStatusEnum.ARCHIVED);
	};
	/**
	 * isArchiveJobDocumentAllowed - Function to check if a job document can be archived
	 *
	 * ALLOWED:
	 * - Provided the job document is not already archived
	 * - Then, always if the job document is a quote
	 * - Then, if the job document is a quote, only if it is not booked
	 */
	const isArchiveJobDocumentAllowed = (
		jobDocument: JobDocumentEntityType
	): PermissionStatus => {
		if (jobDocument.status === JobStatusEnum.ARCHIVED) {
			return {
				isAllowed: false,
				explanation: "Das Dokument ist bereits ungültig.",
			};
		}
		if (
			(jobDocumentIsDeliveryNote(jobDocument) ||
				jobDocumentIsCreditNote(jobDocument)) &&
			(jobDocumentIsBooked(jobDocument) || jobDocumentIsPaid(jobDocument))
		) {
			return {
				isAllowed: false,
				explanation: "Das Dokument ist bereits gebucht.",
			};
		}

		return {
			isAllowed: true,
			explanation: "",
		};
	};
	/**
	 * isExportJobDocumentAllowed - Function to check if a job document can be exported
	 *
	 * @param {JobDocumentWithFiles | JobDocumentEntityType} jobDocument
	 * @param {JobItemEntityType[]} jobItems
	 *
	 * ALLOWED:
	 * - If the job document is in progress OR completed without PDF (to allow PDF export after XML export)
	 * - If the job document has job items
	 *
	 * NOT ALLOWED:
	 * - If the document is booked or paid (use re-export for those cases)
	 * - If a PDF already exists for this document type
	 */
	const isExportJobDocumentAllowed = (
		jobDocument: JobDocumentWithFiles | JobDocumentEntityType,
		jobItems: JobItemEntityType[]
	): PermissionStatus => {
		if (jobItems.length === 0) {
			return {
				isAllowed: false,
				explanation: "Das Dokument hat keine Positionen.",
			};
		}
		if (jobDocumentIsBooked(jobDocument) || jobDocumentIsPaid(jobDocument)) {
			return {
				isAllowed: false,
				explanation: "Das Dokument wurde bereits verbucht oder bezahlt. Es kann allerdings re-exportiert werden, diese Option finden Sie bei den drei Punkten oben rechts.",
			};
		}
		if (jobDocumentHasPdfFile(jobDocument)) {
			return {
				isAllowed: false,
				explanation: "PDF wurde bereits exportiert.",
			};
		}
		if (
			jobDocument.status !== JobStatusEnum.IN_PROGRESS &&
			jobDocument.status !== JobStatusEnum.COMPLETED
		) {
			return {
				isAllowed: false,
				explanation: "Das Dokument kann nicht exportiert werden.",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	/** Exporting an Auftragsblatt is always allowed if the document is a quotation or delivery note */
	const isExportAuftragsblattAllowed = (
		jobDocument: JobDocumentEntityType
	): PermissionStatus => {
		if (
			jobDocumentIsQuotation(jobDocument) ||
			jobDocumentIsDeliveryNote(jobDocument)
		) {
			return { isAllowed: true, explanation: "" };
		} else {
			return {
				isAllowed: false,
				explanation:
					"Das Dokument ist kein Kostenvoranschlag oder Lieferschein.",
			};
		}
	};

	const isExportRechnungAllowed = (
		jobDocument: JobDocumentEntityType
	): PermissionStatus => {
		if (jobDocument.type !== JobDocumentTypeEnum.DeliveryNote) {
			return {
				isAllowed: false,
				explanation: "Diese Funktion ist derzeit nicht verfügbar.",
			};
		}
		if (jobDocument.status !== JobStatusEnum.COMPLETED) {
			return {
				isAllowed: false,
				explanation:
					"Die Rechnung kann erst nach Abschluss des Auftrags erstellt werden.",
			};
		}
		if (jobDocumentIsBooked(jobDocument)) {
			return {
				isAllowed: false,
				explanation: "Das Dokument wurde bereits verbucht.",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	const isExportKartenzahlungAllowed = (
		jobDocument: JobDocumentEntityType,
		amount: number
	): PermissionStatus => {
		const rechnungStatus = isExportRechnungAllowed(jobDocument);
		if (!rechnungStatus.isAllowed) {
			return rechnungStatus;
		}
		if (amount < 0) {
			return {
				isAllowed: false,
				explanation: "Kartenzahlungsquittung kann nicht mit negativen Beträgen erstellt werden.",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	const isExportBarzahlungAllowed = (
		jobDocument: JobDocumentEntityType,
		amount: number
	): PermissionStatus => {
		const rechnungStatus = isExportRechnungAllowed(jobDocument);
		if (!rechnungStatus.isAllowed) {
			return rechnungStatus;
		}
		if (amount < 0) {
			return {
				isAllowed: false,
				explanation: "Barzahlungsquittung kann nicht mit negativen Beträgen erstellt werden.",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	const isExportSwissXmlAllowed = (
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentEntityType
	): PermissionStatus => {
		if (jobItems.length <= 0) {
			return {
				isAllowed: false,
				explanation: "Keine Positionen vorhanden",
			};
		}
		if (jobDocumentIsArchived(jobDocument)) {
			return {
				isAllowed: false,
				explanation: "Das Dokument wurde bereits archiviert.",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};
	
	const isExportGermanXmlAllowed = (
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentEntityType
	): PermissionStatus => {
		if (jobItems.length <= 0) {
			return {
				isAllowed: false,
				explanation: "Keine Positionen vorhanden",
			};
		}
		if (jobDocumentIsArchived(jobDocument)) {
			return {
				isAllowed: false,
				explanation: "Das Dokument wurde bereits archiviert.",
			};
		}
		if (!jobDocumentIsBooked(jobDocument) && !jobDocumentIsPaid(jobDocument)) {
			return {
				isAllowed: false,
				explanation: "Das Dokument wurde noch nicht verbucht oder bezahlt.",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	/**
	 * isReExportLieferscheinAllowed - Function to check if a delivery note can be re-exported
	 *
	 * This is specifically for delivery notes that are already completed, booked, or paid.
	 * Re-export allows users to correct mistakes (typos, wrong patient, etc.) in already
	 * completed/booked documents without having to create an entirely new job and delivery note (that
	 * potentially needs to either be then invalidated again or needs to be corrected via a credit note)
	 * Additionally, this allows to send the corrected document in the same job channel as the original document.
	 *
	 * The re-export ONLY generates a new PDF file - it does NOT change any accounting data,
	 * booking status, or amounts. This keeps the accounting intact while allowing document corrections.
	 *
	 * ALLOWED:
	 * - If the document is a delivery note
	 * - If the document status is completed, booked (single or monthly), or paid (single or monthly)
	 * - If there are job items
	 * - If the stored amount matches the currently calculated amount
	 */
	const isReExportLieferscheinAllowed = (
		jobDocument: JobDocumentEntityType,
		jobItems: JobItemEntityType[],
		calculatedAmount: number
	): PermissionStatus => {
		if (jobDocument.type !== JobDocumentTypeEnum.DeliveryNote) {
			return {
				isAllowed: false,
				explanation: "Nur Lieferscheine können erneut exportiert werden.",
			};
		}
		if (jobItems.length === 0) {
			return {
				isAllowed: false,
				explanation: "Das Dokument hat keine Positionen.",
			};
		}
		if (
			jobDocument.status !== JobStatusEnum.COMPLETED &&
			jobDocument.status !== JobStatusEnum.BOOKED_SINGLE &&
			jobDocument.status !== JobStatusEnum.BOOKED_MONTHLY &&
			jobDocument.status !== JobStatusEnum.PAID_SINGLE &&
			jobDocument.status !== JobStatusEnum.PAID_MONTHLY
		) {
			return {
				isAllowed: false,
				explanation: "Nur abgeschlossene, gebuchte oder bezahlte Lieferscheine können erneut exportiert werden.",
			};
		}
		if (jobDocument.amount !== calculatedAmount) {
			const difference = jobDocument.amount ? applyRounding(jobDocument.amount - calculatedAmount, RoundToType.TO_FIRST_DECIMAL) : 0;
			return {
				isAllowed: false,
				explanation: "Es haben Änderungen stattgefunden, die den Betrag beeinflusst haben. (Differenz: " + difference + ")",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	/**
	 * isReExportKostenvoranschlagAllowed - Function to check if a quotation can be re-exported
	 *
	 * This allows quotations to be exported as PDF after they have been marked as completed
	 * (e.g., after XML export for insurance submission).
	 *
	 * The re-export ONLY generates a new PDF file - it does NOT change any status or amounts.
	 *
	 * ALLOWED:
	 * - If the document is a quotation
	 * - If the document status is completed
	 * - If there are job items
	 * - If the stored amount matches the currently calculated amount
	 */
	const isReExportKostenvoranschlagAllowed = (
		jobDocument: JobDocumentEntityType,
		jobItems: JobItemEntityType[],
		calculatedAmount: number
	): PermissionStatus => {
		if (jobDocument.type !== JobDocumentTypeEnum.Quotation) {
			return {
				isAllowed: false,
				explanation: "Nur Kostenvoranschläge können erneut exportiert werden.",
			};
		}
		if (jobItems.length === 0) {
			return {
				isAllowed: false,
				explanation: "Das Dokument hat keine Positionen.",
			};
		}
		if (jobDocument.status !== JobStatusEnum.COMPLETED) {
			return {
				isAllowed: false,
				explanation: "Nur abgeschlossene Kostenvoranschläge können erneut exportiert werden.",
			};
		}
		if (jobDocument.amount !== calculatedAmount) {
			const difference = jobDocument.amount ? applyRounding(jobDocument.amount - calculatedAmount, RoundToType.TO_FIRST_DECIMAL) : 0;
			return {
				isAllowed: false,
				explanation: "Es haben Änderungen stattgefunden, die den Betrag beeinflusst haben. (Differenz: " + difference + ")",
			};
		}
		return {
			isAllowed: true,
			explanation: "",
		};
	};

	return {
		isArchiveJobDocumentAllowed,
		isExportJobDocumentAllowed,
		archiveJobDocument,
		isExportAuftragsblattAllowed,
		isExportRechnungAllowed,
		isExportKartenzahlungAllowed,
		isExportBarzahlungAllowed,
		isExportSwissXmlAllowed,
		isExportGermanXmlAllowed,
		isReExportLieferscheinAllowed,
		isReExportKostenvoranschlagAllowed,
	};
};
